import axios from "axios";
import store from "@/store";
import router from "@/router";

var isLoggingOut = false;
var REPORTS_API_ORIGIN = "https://reports.androvega.ca";

function requestUrl(config) {
  var baseURL = config?.baseURL || import.meta.env.VUE_APP_API_URL;
  return new URL(config?.url || "", baseURL || window.location.origin);
}

function isMainApiRequest(config) {
  try {
    var baseURL = config?.baseURL || import.meta.env.VUE_APP_API_URL;
    if (!baseURL) return true;
    var base = new URL(baseURL, window.location.origin);
    return requestUrl(config).origin === base.origin;
  } catch (error) {
    return true;
  }
}

function isReportsApiRequest(config) {
  try {
    return requestUrl(config).origin === new URL(REPORTS_API_ORIGIN).origin;
  } catch (error) {
    return false;
  }
}

function isInventoryRequest(config) {
  try {
    return requestUrl(config).pathname.replace(/^\//, "").startsWith("inventory/");
  } catch (error) {
    return false;
  }
}

function removeAuthorizationHeader(config) {
  if (!config.headers) return;
  if (typeof config.headers.delete === "function") {
    config.headers.delete("Authorization");
    config.headers.delete("authorization");
    return;
  }
  delete config.headers.Authorization;
  delete config.headers.authorization;
  if (config.headers.common) {
    delete config.headers.common.Authorization;
    delete config.headers.common.authorization;
  }
}

function setAuthorizationHeader(config, token) {
  config.headers = config.headers || {};
  if (typeof config.headers.set === "function") {
    config.headers.set("Authorization", "Bearer " + token);
    return;
  }
  config.headers.Authorization = "Bearer " + token;
}

var instance = axios.create({
  baseURL: import.meta.env.VUE_APP_API_URL,
  // Without this a stalled request hangs forever and the global loading
  // overlay never clears, so the panel looks frozen with no way back.
  // Some report queries legitimately take seconds, hence 30s rather than less.
  timeout: 30000,
  headers: {
    common: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
  },
});

// Attach token dynamically on every request
instance.interceptors.request.use(function (config) {
  var token = localStorage.getItem("token");
  if (token && (isMainApiRequest(config) || isReportsApiRequest(config))) {
    setAuthorizationHeader(config, token);
  } else {
    removeAuthorizationHeader(config);
  }
  return config;
});

// Redirect to login on 401 (skip for inventory endpoints that use separate auth)
instance.interceptors.response.use(
  function (response) {
    return response;
  },
  function (error) {
    // A 403 from our API says exactly what is missing ("...needs the 'menus.publish'
    // permission" / "That store is not one of yours"). Surface that text instead of
    // axios's generic "Request failed with status code 403", and put it on the
    // error too so pages that toast e.message show the same words.
    if (
      error.response &&
      error.response.status === 403 &&
      isMainApiRequest(error.config) &&
      error.response.data &&
      error.response.data.message
    ) {
      error.name = "Not allowed";
      error.message = error.response.data.message;
      error.toasted = true;
      // Background polls (chat, approvals, assistant status) say nothing; a page's own action does.
      if (!(error.config && error.config.silent)) store.commit("NOTIFICATION_MESSAGE", { status: -1, title: "No permission", message: error.response.data.message });
    }
    if (error.response && error.response.status === 401) {
      // Skip logout for inventory API endpoints (they use separate auth)
      if (
        isMainApiRequest(error.config) &&
        !isInventoryRequest(error.config) &&
        !isLoggingOut
      ) {
        isLoggingOut = true;
        store.dispatch("logoutService").finally(function () {
          isLoggingOut = false;
          router.push("/login").catch(function () {});
        });
      }
    }
    return Promise.reject(error);
  }
);

export default instance;
